/**
 * Timeline Options Composable
 * Manages timeline configuration and date range options
 */

import {
    ref, watch, type Ref
} from 'vue';
import type { TimeFormatPreference } from '@/utils/timeFormat';

interface TimelineOptions {
    editable: {
        add: boolean;
        updateTime: boolean;
        updateGroup: boolean;
        remove: boolean;
    };
    orientation: string;
    stack: boolean;
    showCurrentTime: boolean;
    showMajorLabels?: boolean;
    showMinorLabels?: boolean;
    zoomMin: number;
    zoomMax: number;
    timeAxis: {
        scale: string;
        step: number;
    };
    format: {
        minorLabels: {
            hour: string;
            day: string;
        };
        majorLabels: {
            hour: string;
            day: string;
        };
    };
    min: Date | undefined;
    max: Date | undefined;
    start: Date | undefined;
    end: Date | undefined;
    horizontalScroll: boolean;
    zoomKey: string;
    verticalScroll?: boolean;
    maxHeight: string;
}

/**
 * Composable for managing timeline options and date ranges
 * @param initialVisibleDays - Number of days to show initially (default: 7)
 * @param timezone - Optional timezone (currently not used for axis labels - vis-timeline uses browser timezone)
 * @param timeFormatPreference - User's time format preference (12h or 24h)
 * @returns Timeline options and utility functions
 */
export const useTimelineOptions = (
    initialVisibleDays: number = 7,
    _timezone?: Ref<string | undefined>,
    timeFormatPreference?: Ref<TimeFormatPreference | null>
) => {
    const visibleDays = ref<number>(initialVisibleDays);

    // Determine format based on preference (default to 24h)
    const is12HourFormat = timeFormatPreference?.value?.timeFormat === '12h';
    const hourFormat = is12HourFormat ? 'hh:mm A' : 'HH:mm';

    const options = ref<TimelineOptions>({
        editable: {
            add: false,
            updateTime: true,
            updateGroup: false,
            remove: false
        },
        orientation: 'top',
        stack: false,
        showCurrentTime: true,
        showMajorLabels: true,
        showMinorLabels: true,
        zoomMin: 1000 * 60 * 60, // 1 hour
        zoomMax: 1000 * 60 * 60 * 24 * 30, // 1 month
        timeAxis: { scale: 'hour', step: 4 },
        format: {
            minorLabels: {
                hour: hourFormat,
                day: 'DD'
            },
            majorLabels: {
                hour: 'MMM DD',
                day: 'MMMM YYYY'
            }
        },
        // min/max/start/end left undefined until we have data
        min: undefined,
        max: undefined,
        start: undefined,
        end: undefined,
        horizontalScroll: true,
        zoomKey: 'ctrlKey',
        verticalScroll: true,
        maxHeight: 'none'
    });

    // Watch for time format preference changes and update the axis format
    if (timeFormatPreference) {
        watch(timeFormatPreference, (newPref) => {
            const is12Hour = newPref?.timeFormat === '12h';
            const newHourFormat = is12Hour ? 'hh:mm A' : 'HH:mm';
            options.value.format.minorLabels.hour = newHourFormat;
        }, { immediate: true });
    }

    /**
     * Updates the date range for the timeline
     * @param minDate - The minimum date to display
     * @param maxDate - The maximum date to display
     */
    const updateDateRange = (minDate: Date | null, maxDate: Date | null): void => {
        // If we do not have both dates, remove any previously set range to avoid passing null
        if (!minDate || !maxDate) {
            delete options.value.min;
            delete options.value.max;
            delete options.value.start;
            delete options.value.end;
            return;
        }
        
        // Set the full data range and initial visible window
        options.value.min = minDate;
        options.value.max = maxDate;
        options.value.start = minDate;
        
        // Calculate end date based on visible days window
        const endDate = new Date(
            minDate.getTime() + (visibleDays.value * 24 * 60 * 60 * 1000)
        );
        options.value.end = endDate;
    };

    /**
     * Sets the number of visible days in the timeline window
     * @param days - Number of days to display
     */
    const setVisibleDays = (days: number): void => {
        visibleDays.value = days;
    };

    return {
        options,
        visibleDays,
        updateDateRange,
        setVisibleDays
    };
};
